/** Verificações da georreferenciação de um IFC.
 *
 *  O viewer já lê a IfcMapConversion (leituraDoMapa.ts) e confere o CRS
 *  com o índice EPSG (conferirCrs.ts). Aqui junta-se tudo numa lista de
 *  verificações, com o nível LoGeoRef e os erros que mais aparecem em
 *  ficheiros exportados, para quem recebe um modelo saber se pode confiar
 *  na posição antes de o federar.
 *
 *  Fontes: os níveis LoGeoRef 10 a 50 são de Clemen e Görne (2019),
 *  "Level of Georeferencing (LoGeoRef) using IFC for BIM", Journal of
 *  Geodesy, Cartography and Cadastre 10. As verificações seguem o artigo
 *  "The Hidden Minefield of IFC Georeferencing" (T. Krijnen e J.
 *  Gevaert, BIM Business, 22/10/2026), passado a protótipo em Python na
 *  sessão das aulas MDIGC e ensaiado nos IFC da Clínica exportados do
 *  Revit 2026.4.
 *
 *  Nada aqui muda o modelo nem a posição: só diz o que encontrou. A
 *  unidade do mapa, quando o ficheiro se contradiz, não se escolhe (ver
 *  as leituras alternativas em leituraDoMapa.ts).
 *
 *  Puro: recebe o texto (ou o que já se leu) e, para as contas que
 *  precisam da projecção, um Conversor. */

import { findEntityById, splitTopLevelArgs } from '../stepText'
import { lerRef } from '../step/textoStep'
import type { InfoDoCrs } from '@viewer/map/crsDefs'
import { convergenciaGraus, type Conversor } from './convergencia'
import { lerLocalizacaoDoSitio, trueNorth, type LeituraDoMapa, type LocalizacaoDoSitio } from './leituraDoMapa'

export type Gravidade = 'ok' | 'info' | 'aviso' | 'erro'

export type ChaveDaVerificacao = 'nivel' | 'sistema' | 'unidades' | 'escala' | 'origem' | 'rotacao' | 'sitios' | 'longe' | 'cota'

export interface Verificacao {
  chave: ChaveDaVerificacao
  titulo: string
  gravidade: Gravidade
  texto: string
}

export interface ResultadoGeorref {
  /** LoGeoRef: 0, 10, 20, 30, 40 ou 50. */
  nivel: number
  verificacoes: Verificacao[]
}

/** O que a verificação precisa de ler do texto do IFC, além da conversão. */
export interface DadosDeGeorref {
  /** Há morada (IfcPostalAddress) no IfcSite ou no IfcBuilding. */
  temMorada: boolean
  /** IfcSite com latitude e longitude (LoGeoRef 20). */
  sitio: LocalizacaoDoSitio | null
  /** A colocação do IfcSite tem coordenadas que não são zero (LoGeoRef 30),
   *  na unidade do projecto. */
  posicaoDoSitio: [number, number, number] | null
  /** TrueNorth do contexto (graus a partir de +Y, sentido horário). */
  trueNorthGraus: number | null
  /** O WorldCoordinateSystem do contexto não está na origem. */
  wcsDeslocado: boolean
}

/** Limiares, em metros. */
export const DISTANCIA_ENTRE_SITIOS_M = 50
export const LONGE_AVISO_M = 10_000
export const LONGE_ERRO_M = 100_000

function args(texto: string, id: number | null): { classe: string; a: string[] } | null {
  if (id === null) return null
  const e = findEntityById(texto, id)
  return e ? { classe: e.type, a: splitTopLevelArgs(e.args).map((x) => x.trim()) } : null
}

function coordenadas(texto: string, idPonto: number | null): number[] {
  const p = args(texto, idPonto)
  if (!p || p.classe !== 'IFCCARTESIANPOINT') return []
  return splitTopLevelArgs((p.a[0] ?? '').replace(/^\(|\)$/g, '')).map((x) => Number(x.trim()))
}

/** O ponto de origem de um IfcAxis2Placement (2D ou 3D). */
function origemDoPlacement(texto: string, idPlacement: number | null): number[] {
  const pl = args(texto, idPlacement)
  if (!pl || !pl.classe.startsWith('IFCAXIS2PLACEMENT')) return []
  return coordenadas(texto, lerRef(pl.a[0]))
}

export function lerDadosDeGeorref(texto: string): DadosDeGeorref {
  let temMorada = false
  let posicaoDoSitio: DadosDeGeorref['posicaoDoSitio'] = null
  for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCSITE\s*\(/g)) {
    const s = args(texto, parseInt(m[1], 10))
    if (!s) continue
    // SiteAddress é o 14.º atributo do IfcSite.
    if (args(texto, lerRef(s.a[13]))?.classe === 'IFCPOSTALADDRESS') temMorada = true
    // ObjectPlacement: IfcLocalPlacement(PlacementRelTo, RelativePlacement).
    const lp = args(texto, lerRef(s.a[5]))
    if (lp?.classe === 'IFCLOCALPLACEMENT' && posicaoDoSitio === null) {
      const o = origemDoPlacement(texto, lerRef(lp.a[1]))
      if (o.some((c) => Number.isFinite(c) && c !== 0)) posicaoDoSitio = [o[0] ?? 0, o[1] ?? 0, o[2] ?? 0]
    }
  }
  if (!temMorada) {
    for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCBUILDING\s*\(/g)) {
      // BuildingAddress é o 12.º atributo do IfcBuilding.
      if (args(texto, lerRef(args(texto, parseInt(m[1], 10))?.a[11]))?.classe === 'IFCPOSTALADDRESS') temMorada = true
    }
  }
  let wcsDeslocado = false
  for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCGEOMETRICREPRESENTATIONCONTEXT\s*\(/g)) {
    const c = args(texto, parseInt(m[1], 10))
    const o = origemDoPlacement(texto, lerRef(c?.a[4]))
    if (o.some((v) => Number.isFinite(v) && v !== 0)) wcsDeslocado = true
  }
  return { temMorada, sitio: lerLocalizacaoDoSitio(texto), posicaoDoSitio, trueNorthGraus: trueNorth(texto), wcsDeslocado }
}

/** Número com vírgula decimal e sem zeros à direita. Não usa o
 *  toLocaleString: no Node não agrupa nem formata como no browser, e os
 *  testes deixavam de dizer o mesmo que o ecrã. */
function fmt(v: number, casas = 3): string {
  if (!Number.isFinite(v)) return '?'
  let t = v.toFixed(casas)
  if (t.includes('.')) t = t.replace(/0+$/, '').replace(/\.$/, '')
  if (Number(t) === 0) t = '0'
  return t.replace('.', ',')
}

function distancia(m: number): string {
  return m >= 1000 ? `${fmt(m / 1000, 1)} km` : `${fmt(m, 2)} m`
}

/** -180 a 180. */
function angulo(a: number): number {
  let x = ((a + 180) % 360 + 360) % 360 - 180
  if (Object.is(x, -0)) x = 0
  return x
}

export interface ContextoDaVerificacao {
  leitura: LeituraDoMapa | null
  dados: DadosDeGeorref
  /** Grelha do CRS (metros) para longitude e latitude, e volta. Null
   *  quando o CRS não se conhece ou o mapa não o reproduz. */
  conversor: Conversor | null
  /** Distância da geometria à origem do ficheiro, em metros (a
   *  translação que o Fragments aplica), ou null. */
  distanciaGeometriaM: number | null
}

/** Quando o ficheiro se contradiz sobre a unidade do mapa, a única
 *  leitura plausível (tamanho real e origem dentro da área de uso), se
 *  houver só uma. Não se aplica no modelo; serve para as verificações
 *  seguintes terem com que trabalhar. */
export function leituraPlausivel(l: LeituraDoMapa): LeituraDoMapa['alternativas'][number] | null {
  const plausiveis = l.alternativas.filter((a) => Math.abs(a.escalaGeometrica - 1) < 0.01 && a.foraDaArea !== true)
  return plausiveis.length === 1 ? plausiveis[0] : null
}

export function verificarGeorref(ctx: ContextoDaVerificacao): ResultadoGeorref {
  const { dados: d, conversor } = ctx
  const lida = ctx.leitura
  // Com uma contradição de unidades e uma só leitura plausível, as
  // verificações de origem, rotação e sítios correm com essa leitura.
  const plausivel = lida && !lida.origemEmMetros ? leituraPlausivel(lida) : null
  const l: LeituraDoMapa | null =
    lida && plausivel ? { ...lida, origemEmMetros: plausivel.origemEmMetros, escalaGeometrica: plausivel.escalaGeometrica } : lida
  const v: Verificacao[] = []
  const add = (chave: ChaveDaVerificacao, titulo: string, gravidade: Gravidade, texto: string) =>
    v.push({ chave, titulo, gravidade, texto })
  const info: InfoDoCrs | null = l?.crsInfo ?? null
  const comEntidade = !!l && l.conversao.classe !== 'EPSET_MAPCONVERSION'

  // --- 1. Nível LoGeoRef ------------------------------------------------
  let nivel = 0
  if (d.temMorada) nivel = 10
  if (d.sitio) nivel = 20
  if (d.posicaoDoSitio) nivel = 30
  if (d.trueNorthGraus !== null || d.wcsDeslocado || (l && !comEntidade)) nivel = 40
  if (comEntidade && l?.crs) nivel = 50
  const descricao: Record<number, string> = {
    0: 'sem nenhuma informação de localização',
    10: 'só a morada',
    20: 'latitude e longitude no IfcSite',
    30: 'coordenadas na colocação do IfcSite',
    40: 'sistema de coordenadas e norte verdadeiro no contexto',
    50: 'IfcMapConversion com IfcProjectedCRS',
  }
  let textoNivel = `LoGeoRef ${nivel}: ${descricao[nivel]}.`
  if (l && !comEntidade) {
    textoNivel += ` Há um ePset_MapConversion (IFC2X3): um conjunto de propriedades e não uma entidade, que a plataforma lê mas poucos programas leem.`
  } else if (nivel < 50) {
    textoNivel += ' Sem IfcMapConversion, a posição no mapa depende do IfcSite ou não existe.'
  }
  add('nivel', 'Nível de georreferenciação', nivel === 50 ? 'ok' : nivel >= 20 ? 'aviso' : nivel === 0 ? 'info' : 'aviso', textoNivel)

  if (l) {
    // --- 2. Sistema de coordenadas --------------------------------------
    const nome = l.crs?.nome ?? null
    if (!nome) {
      add('sistema', 'Sistema de coordenadas', 'erro', `A conversão #${l.conversao.id} não nomeia o sistema de coordenadas (TargetCRS).`)
    } else if (!info) {
      add('sistema', 'Sistema de coordenadas', 'erro', `"${nome}" não é um código EPSG reconhecido no registo do PROJ.`)
    } else if (info.geografico) {
      add('sistema', 'Sistema de coordenadas', 'erro', `${info.codigo} (${info.nome}) é geográfico, em graus: a IfcMapConversion precisa de um sistema projectado, em metros ou pés.`)
    } else if (info.codigo === 'EPSG:3857') {
      add('sistema', 'Sistema de coordenadas', 'erro', 'EPSG:3857 (Web Mercator) não serve como sistema do projecto: as distâncias ficam erradas por um factor de cerca de 1/cos(latitude), 30 % em Portugal.')
    } else if (info.naoSuportado) {
      add('sistema', 'Sistema de coordenadas', 'aviso', `${info.codigo} (${info.nome}) existe, mas o mapa não o reproduz: ${info.naoSuportado}`)
    } else {
      add('sistema', 'Sistema de coordenadas', 'ok', `${info.codigo}, ${info.nome}.`)
    }

    // --- 3. Unidades ----------------------------------------------------
    const um = l.unidadeDoMapa
    if (l.alternativas.length > 0) {
      const leituras = l.alternativas
        .map((a) => `${a.rotulo}: ${fmt(a.escalaGeometrica, 6)} vezes o tamanho real${a.foraDaArea ? ', origem fora da área de uso' : ''}`)
        .join('; ')
      add(
        'unidades',
        'Unidades do mapa',
        'erro',
        `O ficheiro contradiz-se sobre a unidade das coordenadas do mapa (${leituras}). ${
          plausivel
            ? `Só a leitura "${plausivel.rotulo}" dá o tamanho real dentro da área de uso: é quase de certeza a intenção, e falta declarar a MapUnit. As verificações abaixo usam essa leitura, mas a plataforma não a aplica sozinha: escolhe-a nas leituras abaixo.`
            : 'Nenhuma leitura se aplica sem escolha: ver as leituras abaixo.'
        }`,
      )
    } else if (comEntidade && um?.origem === 'projecto') {
      const igual = info ? Math.abs(um.metros / info.metros - 1) < 1e-9 : null
      add(
        'unidades',
        'Unidades do mapa',
        igual === false ? 'erro' : 'aviso',
        `MapUnit em falta: vale a unidade do projecto (${um.simbolo}), o que só está certo se for a do sistema${info ? ` (${info.metros === 1 ? 'metro' : `${fmt(info.metros, 10)} m`})` : ''}.${igual === false ? ' Não é: as coordenadas do mapa ficam lidas na unidade errada.' : ''}`,
      )
    } else if (um && info && Math.abs(um.metros / info.metros - 1) > 1e-9) {
      const ppm = (um.metros / info.metros - 1) * 1e6
      add('unidades', 'Unidades do mapa', 'erro', `A unidade do mapa (${fmt(um.metros, 10)} m) não é a do sistema ${info.codigo} (${fmt(info.metros, 10)} m): ${fmt(ppm, 1)} ppm, ${distancia(Math.abs(ppm) * 1e-6 * Math.hypot(l.conversao.eastings * um.metros, l.conversao.northings * um.metros))} na origem.`)
    } else if (um) {
      add('unidades', 'Unidades do mapa', 'ok', `Mapa em ${um.simbolo}${um.origem === 'MapUnit' ? ' (MapUnit)' : ''}, projecto em ${l.unidadeDoProjecto?.simbolo ?? '?'}.`)
    }

    // --- 4. Escala ------------------------------------------------------
    const esc = l.escalaGeometrica
    if (esc !== null && Number.isFinite(esc) && esc > 0) {
      const scale = l.conversao.scale
      const esperado = l.unidadeDoProjecto && um ? l.unidadeDoProjecto.metros / um.metros : null
      if (Math.abs(esc - 1) <= 1e-9) {
        add('escala', 'Escala (Scale)', 'ok', `Scale ${scale === null ? 'omitido (vale 1)' : fmt(scale, 10)}: o modelo fica no tamanho real.`)
      } else if (esc >= 2 || esc <= 0.5) {
        const invertido = esperado !== null && scale !== null && Math.abs(scale * esperado - 1) < 1e-6 && Math.abs(esperado - 1) > 1e-6
        add(
          'escala',
          'Escala (Scale)',
          'erro',
          `Com Scale ${scale === null ? 'omitido (vale 1)' : fmt(scale, 10)}, o modelo fica ${fmt(esc, 6)} vezes o tamanho real no mapa${invertido ? ': o Scale está invertido' : ''}.${esperado !== null ? ` O esperado era ${fmt(esperado, 10)}.` : ''}`,
        )
      } else {
        const ppm = (esc - 1) * 1e6
        add(
          'escala',
          'Escala (Scale)',
          Math.abs(ppm) <= 1000 ? 'aviso' : 'erro',
          `O Scale dá uma escala ${fmt(ppm, 1)} ppm diferente de 1. Só se justifica como o factor de escala da projecção naquele sítio; de outra forma, a 100 m da origem são ${distancia(Math.abs(ppm) * 1e-4)}.`,
        )
      }
    }

    // --- 5. Origem --------------------------------------------------------
    if (l.conversao.eastings === 0 && l.conversao.northings === 0) {
      add(
        'origem',
        'Origem no mapa',
        'erro',
        `Eastings = Northings = 0: a origem fica na própria origem do sistema${info?.codigo === 'EPSG:3763' ? ' (no PT-TM06, em Vila de Rei)' : ''}. É o que sai do Revit ao exportar com o Survey Point ou com as coordenadas partilhadas, ou uma conversão por preencher.`,
      )
    } else if (l.origemEmMetros && conversor && info?.area) {
      const [lon, lat] = conversor.forward([l.origemEmMetros[0], l.origemEmMetros[1]])
      const [w, s, e, n] = info.area
      const fora = !(Number.isFinite(lon) && Number.isFinite(lat)) || lon < w - 0.5 || lon > e + 0.5 || lat < s - 0.5 || lat > n + 0.5
      add(
        'origem',
        'Origem no mapa',
        fora ? 'erro' : 'ok',
        fora
          ? `A origem fica fora da área de uso de ${info.codigo}: sinal de unidades trocadas ou de sistema errado.`
          : `A origem fica em ${fmt(lat, 5)}, ${fmt(lon, 5)}, dentro da área de uso de ${info.codigo}.`,
      )
    }

    // --- 6. Rotação e norte verdadeiro -------------------------------------
    // A plataforma põe um modelo no mapa pelo IfcSite com rotação =
    // TrueNorth + convergência (colocacaoPeloSitio.ts); a mesma relação
    // tem de valer entre o TrueNorth do contexto e a rotação da conversão.
    if (d.trueNorthGraus === null) {
      add('rotacao', 'Norte verdadeiro', 'info', 'Sem TrueNorth no contexto: a rotação da IfcMapConversion não se pode conferir.')
    } else if (l.origemEmMetros && conversor) {
      const c = convergenciaGraus(conversor, l.origemEmMetros[0], l.origemEmMetros[1])
      const esperado = angulo(l.rotacaoGraus - c)
      const dif = angulo(d.trueNorthGraus - esperado)
      const semConvergencia = Math.abs(angulo(d.trueNorthGraus - l.rotacaoGraus)) <= 0.01
      if (Math.abs(dif) <= 0.01) {
        add('rotacao', 'Norte verdadeiro', 'ok', `O TrueNorth (${fmt(d.trueNorthGraus, 3)}°) bate com a rotação da conversão (${fmt(l.rotacaoGraus, 3)}°) descontada a convergência de meridianos (${fmt(c, 3)}°).`)
      } else if (semConvergencia && Math.abs(c) > 0.01) {
        add(
          'rotacao',
          'Norte verdadeiro',
          'aviso',
          `O TrueNorth (${fmt(d.trueNorthGraus, 3)}°) foi escrito igual à rotação da conversão, sem a convergência de meridianos (${fmt(c, 3)}° neste sítio). A posição no mapa não muda, mas quem orientar pelo norte verdadeiro fica rodado ${fmt(Math.abs(c), 3)}°: ${distancia(Math.abs(c) * Math.PI / 180 * 100)} a 100 m da origem.`,
        )
      } else {
        add(
          'rotacao',
          'Norte verdadeiro',
          Math.abs(dif) > 1 ? 'erro' : 'aviso',
          `O TrueNorth (${fmt(d.trueNorthGraus, 3)}°) e a rotação da conversão (${fmt(l.rotacaoGraus, 3)}°) não batem: com a convergência de meridianos (${fmt(c, 3)}°), o TrueNorth devia ser ${fmt(esperado, 3)}° (diferença de ${fmt(dif, 3)}°).`,
        )
      }
    }

    // --- 7. Dois sítios ----------------------------------------------------
    if (d.sitio && l.origemEmMetros && l.escalaGeometrica && conversor && l.unidadeDoProjecto) {
      const p = d.posicaoDoSitio ?? [0, 0, 0]
      const k = l.unidadeDoProjecto.metros
      const t = (l.rotacaoGraus * Math.PI) / 180
      const x = p[0] * k
      const y = p[1] * k
      const eSitio = l.origemEmMetros[0] + l.escalaGeometrica * (Math.cos(t) * x - Math.sin(t) * y)
      const nSitio = l.origemEmMetros[1] + l.escalaGeometrica * (Math.sin(t) * x + Math.cos(t) * y)
      const [eRef, nRef] = conversor.inverse([d.sitio.longitude, d.sitio.latitude])
      const dist = Math.hypot(eRef - eSitio, nRef - nSitio)
      if (!Number.isFinite(dist)) {
        add('sitios', 'IfcSite e conversão', 'aviso', 'A latitude e a longitude do IfcSite não se conseguem levar ao sistema da conversão.')
      } else {
        add(
          'sitios',
          'IfcSite e conversão',
          dist > DISTANCIA_ENTRE_SITIOS_M ? 'erro' : 'ok',
          dist > DISTANCIA_ENTRE_SITIOS_M
            ? `A latitude e a longitude do IfcSite (${fmt(d.sitio.latitude, 5)}, ${fmt(d.sitio.longitude, 5)}) ficam a ${distancia(dist)} da posição que a IfcMapConversion dá ao sítio. Um dos dois está errado; costuma ser o IfcSite, esquecido num valor por omissão.`
            : `A latitude e a longitude do IfcSite batem com a conversão (${distancia(dist)} de diferença).`,
        )
      }
    } else if (!d.sitio && comEntidade) {
      add('sitios', 'IfcSite e conversão', 'info', 'O IfcSite não tem latitude e longitude para conferir com a conversão.')
    }

    // --- 9. Cota ----------------------------------------------------------
    if (l.origemEmMetros) {
      const h = l.origemEmMetros[2]
      const el = d.sitio?.elevacaoMetros ?? null
      add(
        'cota',
        'Cota',
        el !== null && Math.abs(el - h) > 1 ? 'aviso' : 'info',
        `OrthogonalHeight = ${fmt(h, 3)} m${el !== null ? `; RefElevation do IfcSite = ${fmt(el, 3)} m${Math.abs(el - h) > 1 ? ' (não coincidem)' : ''}` : ''}. O IFC não diz se a cota é altitude ou altura elipsoidal: no globo, a plataforma trata-a como altitude e corrige o geoide (EGM96).`,
      )
    }
  } else if (d.sitio) {
    add('sistema', 'Sistema de coordenadas', 'aviso', 'Sem IfcMapConversion: a posição no mapa vem só da latitude e longitude do IfcSite, numa grelha calculada (UTM), e não de um sistema declarado.')
  }

  // --- 8. Geometria longe da origem ----------------------------------------
  const dg = ctx.distanciaGeometriaM
  if (dg !== null && Number.isFinite(dg)) {
    if (dg > LONGE_ERRO_M) {
      add('longe', 'Geometria e origem do ficheiro', 'erro', `A geometria está a ${distancia(dg)} da origem do ficheiro: em precisão simples, os passos já são de vários milímetros (cerca de 8 mm a 100 km). A posição no mapa deve ir na IfcMapConversion, com a geometria perto da origem.`)
    } else if (dg > LONGE_AVISO_M) {
      add('longe', 'Geometria e origem do ficheiro', 'aviso', `A geometria está a ${distancia(dg)} da origem do ficheiro (cerca de 1 mm de precisão a 10 km). Preferir a geometria perto da origem e o deslocamento na IfcMapConversion.`)
    } else {
      add('longe', 'Geometria e origem do ficheiro', 'ok', `A geometria está perto da origem do ficheiro (${distancia(dg)}).`)
    }
  }

  return { nivel, verificacoes: v }
}

const resultados = new Map<string, ResultadoGeorref>()
const ouvintes = new Set<() => void>()

/** O resultado chega depois de o modelo estar na lista (a verificação
 *  espera pela geometria): quem o mostra subscreve para redesenhar. */
export function ouvirVerificacoes(cb: () => void): () => void {
  ouvintes.add(cb)
  return () => {
    ouvintes.delete(cb)
  }
}

export function guardarVerificacoes(modelId: string, r: ResultadoGeorref | null): void {
  if (r) resultados.set(modelId, r)
  else resultados.delete(modelId)
  for (const cb of ouvintes) cb()
}
export function verificacoesGuardadas(modelId: string): ResultadoGeorref | undefined {
  return resultados.get(modelId)
}

/** A pior gravidade de um resultado, para um resumo de uma palavra. */
export function gravidadeGeral(r: ResultadoGeorref): Gravidade {
  if (r.verificacoes.some((v) => v.gravidade === 'erro')) return 'erro'
  if (r.verificacoes.some((v) => v.gravidade === 'aviso')) return 'aviso'
  return 'ok'
}
